import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface ProfileGroupAssociation {
  profileId: number;
  profileName: string;
  groupId: number;
  groupName: string;
  groupDescription?: string;
  createdAt: string;
  createdBy: string;
}

export interface ProfileGroupAssociationResponse {
  data: ProfileGroupAssociation[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface AssociateGroupsRequest {
  profileId: number;
  groupIds: number[];
  updatedBy: string;
}

export interface RemoveGroupsRequest {
  profileId: number;
  groupIds: number[];
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileGroupAssociationsService {
  private baseUrl = environment.microservices.profileGroup;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
  }

  // ===== Debug Functions =====

  /**
   * Busca todas as associações Profile-Group
   */
  getProfileGroupAssociations(page: number = 1, pageSize: number = 10): Observable<ProfileGroupAssociationResponse> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileGroupAssociationResponse>(`${this.baseUrl}/GetProfileGroupAssociations`, { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar associações Profile-Group');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca associações de um perfil específico
   */
  getProfileGroupAssociationsByProfile(profileId: number, page: number = 1, pageSize: number = 10): Observable<ProfileGroupAssociationResponse> {
       const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<ProfileGroupAssociationResponse>(`${this.baseUrl}/GetProfileGroupAssociations`, { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, `Erro ao buscar associações do perfil ${profileId}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca todas as associações sem paginação
   */
  getAllProfileGroupAssociations(): Observable<ProfileGroupAssociation[]> {
    return this.http.get<ProfileGroupAssociation[]>(`${this.baseUrl}/GetProfileGroupAssociations?pageSize=1000`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao buscar todas as associações');
        return throwError(() => err);
      })
    );
  }

  // ===== Association Management =====

  /**
   * Associa grupos a um perfil existente
   */
  associateGroupsToProfile(request: AssociateGroupsRequest): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/AssociateGroupsToProfile`, request).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao associar grupos ao perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove grupos de um perfil
   */
  removeGroupsFromProfile(request: RemoveGroupsRequest): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/RemoveGroupsFromProfile`, {
      body: request
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao remover grupos do perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Verifica se um grupo está associado a um perfil
   */
  isGroupAssociatedToProfile(profileId: number, groupId: number): Observable<{ isAssociated: boolean }> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('groupId', groupId.toString());

    return this.http.get<{ isAssociated: boolean }>(`${this.baseUrl}/IsGroupAssociated`, { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao verificar associação');
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca grupos associados a um perfil
   */
  getGroupsByProfile(profileId: number, page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('profileId', profileId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetGroupsByProfile`, { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, `Erro ao buscar grupos do perfil ${profileId}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Busca perfis associados a um grupo
   */
  getProfilesByGroup(groupId: number, page: number = 1, pageSize: number = 10): Observable<any> {
    const params = new HttpParams()
      .set('groupId', groupId.toString())
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(`${this.baseUrl}/GetProfilesByGroup`, { params }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, `Erro ao buscar perfis do grupo ${groupId}`);
        return throwError(() => err);
      })
    );
  }

  /**
   * Conta quantos grupos estão associados a um perfil
   */
  countGroupsByProfile(profileId: number): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/CountGroupsByProfile/${profileId}`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao contar grupos do perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Conta quantos perfis estão associados a um grupo
   */
  countProfilesByGroup(groupId: number): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.baseUrl}/CountProfilesByGroup/${groupId}`).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao contar perfis do grupo');
        return throwError(() => err);
      })
    );
  }

  // ===== Bulk Operations =====

  /**
   * Associa múltiplos grupos a múltiplos perfis
   */
  bulkAssociateGroupsToProfiles(associations: AssociateGroupsRequest[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/BulkAssociateGroupsToProfiles`, associations).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao associar grupos em lote');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove múltiplas associações de uma vez
   */
  bulkRemoveGroupsFromProfiles(removals: RemoveGroupsRequest[]): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/BulkRemoveGroupsFromProfiles`, removals).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao remover associações em lote');
        return throwError(() => err);
      })
    );
  }

  /**
   * Substitui todos os grupos de um perfil
   */
  replaceProfileGroups(profileId: number, groupIds: number[], updatedBy: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/ReplaceProfileGroups`, {
      profileId,
      groupIds,
      updatedBy
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao substituir grupos do perfil');
        return throwError(() => err);
      })
    );
  }

  // ===== Export/Import =====

  /**
   * Exporta associações para backup/auditoria
   */
  exportAssociations(format: 'json' | 'csv' = 'json'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    
    return this.http.get(`${this.baseUrl}/ExportProfileGroupAssociations`, {
      params,
      responseType: 'blob'
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao exportar associações');
        return throwError(() => err);
      })
    );
  }

  /**
   * Importa associações de um arquivo
   */
  importAssociations(file: File, importedBy: string): Observable<{ success: boolean; message: string; imported: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('importedBy', importedBy);

    return this.http.post<{ success: boolean; message: string; imported: number }>(
      `${this.baseUrl}/ImportProfileGroupAssociations`,
      formData
    ).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao importar associações');
        return throwError(() => err);
      })
    );
  }
}
